// ./src/components/features/UserInvitations/UserInvitationsList/UserInvitationsList.tsx
//
// List component for the UserInvitations page.
// Renders invitations as custom cards with swipeable actions on mobile
// and hover actions on desktop — matching the UsersList pattern.
//
// Layout design (desktop, sm+):
//   ┌──────────────────────────────────────────────────────────────┐
//   │  [AV]  First Last     ADMIN   ● Active / ⚠ Expired  [↻][✏][🗑]│
//   │        ┌──────────────────┬──────────────────────────┐      │
//   │        │ ✉ EMAIL          │ 📅 EXPIRES               │      │
//   │        │ user@example.com │ Mar 15, 2026             │      │
//   │        └──────────────────┴──────────────────────────┘      │
//   └──────────────────────────────────────────────────────────────┘
//
// Layout design (mobile, <sm):
//   ┌──────────────────────────┐
//   │  [AV]  First Last       │  ← swipeable for actions
//   │  ADMIN   ● Active       │
//   │  ✉ EMAIL                │
//   │  user@example.com       │
//   │  📅 EXPIRES              │
//   │  Mar 15, 2026           │
//   └──────────────────────────┘
//
// Context support:
// - Reads from UserInvitationsProvider when available; props override context.

import React, { useMemo, useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Box,
  ActionIcon,
  Tooltip,
  SimpleGrid,
  Skeleton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { formatDateTime } from "@/utils/dateHelpers";

import { UserInvitation } from "@/utils/api/UserInvitationService";

import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

import { useOptionalUserInvitationsContext } from "@/contexts/UserInvitationsContext";

import { AppIcon } from "@/components/generic/AppIcon";
import { EmptyState } from "@/components/generic/EmptyState";
import { LoadMoreButton } from "@/components/generic/LoadMoreButton";
import { SwipeableCard } from "@/components/generic/SwipeableCard";
import { SwipeAction, useSwipeableList } from "@/components/generic/SwipeableCard";

// ============================================
// Types
// ============================================

export interface UserInvitationsListProps {
  /** Array of invitations to display. Optional — reads from context. */
  userInvitations?: UserInvitation[];

  /** Loading state for initial load. Optional — reads from context. */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEdit?: (invitation: UserInvitation) => void;

  /** Callback when delete action is clicked */
  onDelete?: (invitation: UserInvitation) => void;

  /** Callback when resend action is clicked */
  onResend?: (invitation: UserInvitation) => void;

  /** ID of invitation currently being removed. Optional — reads from context. */
  removingId?: string | null;

  /** ID of invitation currently being resent. Optional — reads from context. */
  resendingId?: string | null;

  /** ID of recently edited/added invitation to highlight */
  highlightedId?: string | null;

  /** Callback when add/invite button is clicked (for empty state) */
  onAdd?: () => void;
}

// ============================================
// CSS for hover actions (injected once)
// ============================================

const ACTIONS_CLASS = "invitation-card__actions";
const CARD_CLASS = "invitation-card";

const CARD_STYLES = `
  .${ACTIONS_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }
  @media (hover: hover) {
    .${CARD_CLASS}:hover .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
  .${CARD_CLASS}:focus-within .${ACTIONS_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }
  @media (hover: none) {
    .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-invitation-card", "");
  style.textContent = CARD_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
};

// ============================================
// Loading Skeleton
// ============================================

const LoadingSkeleton: React.FC = () => {
  return (
    <Stack gap="md">
      {Array.from({ length: 4 }).map((_, index) => (
        <Paper key={index} p="md" withBorder>
          <Group gap="md" wrap="nowrap" align="flex-start">
            <Skeleton height={44} width={44} circle style={{ flexShrink: 0 }} />
            <Stack gap="sm" style={{ flex: 1 }}>
              <Group justify="space-between">
                <Skeleton height={20} width={180} />
                <Group gap="xs">
                  <Skeleton height={22} width={60} radius="xl" />
                  <Skeleton height={22} width={60} radius="xl" />
                </Group>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                <Skeleton height={36} width="80%" />
                <Skeleton height={36} width="60%" />
              </SimpleGrid>
            </Stack>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
};

// ============================================
// Avatar Circle
// ============================================

interface AvatarCircleProps {
  name: string;
  color?: string;
}

const AvatarCircle: React.FC<AvatarCircleProps> = ({ name, color = "blue" }) => {
  const initials = useMemo(() => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (name[0] || "?").toUpperCase();
  }, [name]);

  return (
    <Box
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `var(--mantine-color-${color}-0)`,
        border: `2px solid var(--mantine-color-${color}-2)`,
        flexShrink: 0,
      }}
    >
      <Text size="md" fw={600} c={color}>
        {initials}
      </Text>
    </Box>
  );
};

// ============================================
// Info Field — icon + label + value
// ============================================

interface InfoFieldProps {
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
  valueFw?: number;
  suffix?: React.ReactNode;
}

const InfoField: React.FC<InfoFieldProps> = ({
  icon,
  label,
  value,
  valueColor,
  valueFw,
  suffix,
}) => (
  <Group gap={8} wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
    <Group gap={4} wrap="nowrap" align="center" style={{ flexShrink: 0, height: 18 }}>
      <AppIcon iconName={icon} size={14} />
    </Group>
    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500} lh={1.3}>
        {label}
      </Text>
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
        <Text size="md" c={valueColor} fw={valueFw} truncate style={{ minWidth: 0 }}>
          {value}
        </Text>
        {suffix}
      </Group>
    </Stack>
  </Group>
);

// ============================================
// Expiration helpers
// ============================================

const isExpired = (expiresAt?: string | null): boolean => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

// ============================================
// Single Invitation Card
// ============================================

interface InvitationCardProps {
  invitation: UserInvitation;
  onClick?: (invitation: UserInvitation) => void;
  onEdit?: (invitation: UserInvitation) => void;
  onDelete?: (invitation: UserInvitation) => void;
  onResend?: (invitation: UserInvitation) => void;
  highlighted: boolean;
  isDeleting: boolean;
  isResending: boolean;
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const InvitationCardComponent: React.FC<InvitationCardProps> = ({
  invitation,
  onClick,
  onEdit,
  onDelete,
  onResend,
  highlighted,
  isDeleting,
  isResending,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["userInvitations", "common"]);

  injectStyles();

  const fullName = [invitation.firstName, invitation.lastName]
    .filter(Boolean)
    .join(" ") || invitation.email || "—";

  const expired = isExpired(invitation.expiresAt);
  const hasActions = !!onEdit || !!onDelete || !!onResend;

  // ── Handlers ──

  const handleClick = useCallback(() => {
    onClick?.(invitation);
  }, [onClick, invitation]);

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit?.(invitation);
    },
    [onEdit, invitation]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(invitation);
    },
    [onDelete, invitation]
  );

  const handleResendClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onResend?.(invitation);
    },
    [onResend, invitation]
  );

  // ── Swipe actions (mobile) ──

  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    if (onResend) {
      actions.push({
        key: "resend",
        icon: "Send",
        label: t("userInvitations:actions.resend", { defaultValue: "Resend" }),
        color: "teal",
        onClick: () => onResend(invitation),
        loading: isResending,
        disabled: isDeleting || isResending,
      });
    }
    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(invitation),
        disabled: isDeleting || isResending,
      });
    }
    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(invitation),
        loading: isDeleting,
        disabled: isDeleting || isResending,
      });
    }
    return actions;
  }, [onResend, onEdit, onDelete, invitation, isDeleting, isResending, t]);

  // ── Hover actions (desktop) ──

  const hoverActionsEl = hasActions && (
    <Group gap={4} wrap="nowrap" className={ACTIONS_CLASS}>
      {onResend && (
        <Tooltip
          label={t("userInvitations:actions.resend", { defaultValue: "Resend invitation" })}
          withArrow
          position="bottom"
        >
          <ActionIcon
            variant="subtle"
            color="teal"
            size="md"
            onClick={handleResendClick}
            loading={isResending}
            disabled={isDeleting || isResending}
            aria-label={t("userInvitations:actions.resend", { defaultValue: "Resend" })}
          >
            <AppIcon iconName="Send" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={handleEditClick}
            disabled={isDeleting || isResending}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={handleDeleteClick}
            loading={isDeleting}
            disabled={isDeleting || isResending}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  // ── Status badge ──

  const statusBadge = expired ? (
    <Badge size="sm" color="red" variant="light">
      {t("userInvitations:status.expired", { defaultValue: "Expired" })}
    </Badge>
  ) : (
    <Badge size="sm" color="green" variant="light">
      {t("userInvitations:status.active", { defaultValue: "Active" })}
    </Badge>
  );

  // ── Expiration display ──

  const expiresFormatted = invitation.expiresAt
    ? formatDateTime(invitation.expiresAt, "date")
    : t("userInvitations:list.noExpiration", { defaultValue: "No expiration" });

  // ── Card content ──

  const cardContent = (
    <Paper
      p="md"
      withBorder
      bg={highlighted ? "green.0" : undefined}
      opacity={isDeleting ? 0.5 : 1}
      className={CARD_CLASS}
      style={{
        cursor: onClick ? "pointer" : undefined,
        transition: "box-shadow 150ms ease, opacity 150ms ease",
      }}
      onClick={handleClick}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {/* Avatar */}
        <AvatarCircle name={fullName} color="blue" />

        {/* Content */}
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: Name + Role + Status + Actions */}
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Group gap="sm" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="lg" truncate style={{ minWidth: 0 }}>
                {fullName}
              </Text>
              {invitation.userRole?.roleName && (
                <Badge variant="light" size="sm" color="violet">
                  {invitation.userRole.roleName}
                </Badge>
              )}
              {statusBadge}
            </Group>
            {/* Desktop hover actions */}
            <Box visibleFrom="sm" style={{ flexShrink: 0 }}>
              {hoverActionsEl}
            </Box>
          </Group>

          {/* Row 2: Info fields in two-column grid on desktop */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
            {invitation.email && (
              <InfoField
                icon="Mail"
                label={t("userInvitations:list.email", { defaultValue: "Email" })}
                value={invitation.email}
              />
            )}
            <InfoField
              icon="Calendar"
              label={t("userInvitations:list.expires", { defaultValue: "Expires" })}
              value={expiresFormatted}
              valueColor={expired ? "red" : undefined}
              valueFw={expired ? 500 : undefined}
            />
          </SimpleGrid>
        </Stack>
      </Group>
    </Paper>
  );

  // ── Mobile: swipeable / Desktop: plain ──

  if (leftSwipeActions.length > 0) {
    return (
      <>
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={leftSwipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-default)"
          >
            {cardContent}
          </SwipeableCard>
        </Box>
        <Box visibleFrom="sm">
          {cardContent}
        </Box>
      </>
    );
  }

  return cardContent;
};

const InvitationCard = React.memo(InvitationCardComponent);

// ============================================
// Main Component
// ============================================

const UserInvitationsListComponent: React.FC<UserInvitationsListProps> = ({
  userInvitations: invitationsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEdit,
  onDelete,
  onResend,
  removingId: removingIdProp,
  resendingId: resendingIdProp,
  highlightedId,
  onAdd,
}) => {
  const { t } = useTranslation(["userInvitations", "common"]);

  // ── Context vs Props resolution ──
  const invitationsContext = useOptionalUserInvitationsContext();

  const invitations = invitationsProp ?? invitationsContext?.userInvitations ?? [];
  const loading = loadingProp ?? invitationsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? invitationsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? invitationsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? invitationsContext?.loadMore;
  const removingId = removingIdProp ?? invitationsContext?.removingId ?? null;
  const resendingId = resendingIdProp ?? invitationsContext?.resendingId ?? null;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation
  const { hasAnimated } = useEntranceAnimation({ itemCount: invitations.length });

  // ── Handlers ──

  const handleClick = useCallback(
    (invitation: UserInvitation) => {
      closeAllCards();
      onEdit?.(invitation);
    },
    [onEdit, closeAllCards]
  );

  const handleEdit = useCallback(
    (invitation: UserInvitation) => {
      closeAllCards();
      onEdit?.(invitation);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (invitation: UserInvitation) => {
      closeAllCards();
      onDelete?.(invitation);
    },
    [onDelete, closeAllCards]
  );

  const handleResend = useCallback(
    (invitation: UserInvitation) => {
      closeAllCards();
      onResend?.(invitation);
    },
    [onResend, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (invitationId: string, isOpen: boolean) => {
      if (isOpen) openCard(invitationId);
    },
    [openCard]
  );

  // ── Loading state ──

  if (loading && invitations.length === 0) {
    return <LoadingSkeleton />;
  }

  // ── Empty state ──

  if (!loading && invitations.length === 0) {
    return (
      <Paper withBorder p="xl">
        <EmptyState
          title={t("userInvitations:emptyState.title", { defaultValue: "No invitations found" })}
          description={t("userInvitations:emptyState.description", {
            defaultValue: "Send invitations to add team members.",
          })}
          iconName="Mail"
          actionLabel={t("userInvitations:emptyState.actionLabel", { defaultValue: "Invite User" })}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-md)",
        }}
      >
        <AnimatePresence mode="sync">
          {invitations.map((invitation) => (
            <motion.div
              key={invitation.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <InvitationCard
                invitation={invitation}
                onClick={onEdit ? handleClick : undefined}
                onEdit={onEdit ? handleEdit : undefined}
                onDelete={onDelete ? handleDelete : undefined}
                onResend={onResend ? handleResend : undefined}
                highlighted={invitation.id === highlightedId}
                isDeleting={removingId === invitation.id}
                isResending={resendingId === invitation.id}
                forceClose={shouldForceClose(invitation.id || "")}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(invitation.id || "")
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(invitation.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <LoadMoreButton
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </Stack>
  );
};

export const UserInvitationsList = React.memo(UserInvitationsListComponent);